import { useMemo, useState, useEffect, useRef } from 'react';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import {
  Table,
  Badge,
  DateCell,
  SearchInput,
  useTable,
  BaseLayout,
  SelectDropdown,
  Spinner,
  Authorizor,
  OfflineSurveys,
} from '@/components';
import ROLES from '@/constants/roles.constants';
import { useAuth } from '../../hooks/use-auth';
import userIsAuthorized from '../../utils/userIsAuthorized';
import { Button, Card, CardContent, Switch } from '@/components/ui';
import { SURVEY_FILTER_VALUES } from '@/constants/survey.constants';
import { SurveyType, type SurveyTableItem } from '@/types/surveys.types';
import type { AccountListItem } from '@/types/accounts.types';
import { getAccounts } from '@/api/account.api';
import { getRouteSurveyCategories } from '@/api/survey-config.api';
import {
  downloadSurveysAsCsv,
  bulkMarkSurveysAsReviewed,
  undoBulkMarkSurveysAsReviewed,
  type SurveyRef,
} from '@/api/survey.api';
import {
  BulkActionBar,
  type BulkAction,
} from '@/components/utility/table/bulk-action-bar';
import type { TableSelectionConfig } from '@/components/utility/table';
import { getHierarchyLevels } from '@/api/account-hierarchy.api';
import type { HierarchyLevelResponse } from '@/types/account-hierarchy.types';
import { Download } from '@carbon/icons-react';
import { useToast } from '@/contexts/toast-context';
import { TOAST_TYPES } from '@/constants/common.constants';
import {
  cacheSurveys,
  getCachedSurveys,
} from '@/services/offline-surveys.service';
import {
  getReviewedBadgeClasses,
  getAlertBadgeType,
  getSurveyListScoreClasses,
  getEnpsScoreClasses,
} from '@/utils/survey.utils';
import './surveys-style.scss';

interface SurveySearchParams {
  // Pagination & Table state (managed by useTable)
  page?: number;
  pageSize?: number;
  sortBy?: string;
  direction?: 'asc' | 'desc';
  searchText?: string;
  // Filters (managed by this component)
  accountUuid?: string;
  dateRange?: string;
  surveyType?: string;
  scoreCategory?: string;
  showAlertsOnly?: boolean;
  showWithCommentsOnly?: boolean;
  showNotViewedOnly?: boolean;
  organizationLevelUuid?: string;
}

export const Route = createFileRoute('/surveys/')({
  component: Surveys,
  validateSearch: (search: Record<string, unknown>): SurveySearchParams => {
    return {
      // Pagination & Table state
      page:
        typeof search.page === 'number'
          ? search.page
          : typeof search.page === 'string'
          ? parseInt(search.page)
          : undefined,
      pageSize:
        typeof search.pageSize === 'number'
          ? search.pageSize
          : typeof search.pageSize === 'string'
          ? parseInt(search.pageSize)
          : undefined,
      sortBy: typeof search.sortBy === 'string' ? search.sortBy : undefined,
      direction:
        search.direction === 'asc' || search.direction === 'desc'
          ? search.direction
          : undefined,
      searchText:
        typeof search.searchText === 'string' ? search.searchText : undefined,
      // Filters
      accountUuid:
        typeof search.accountUuid === 'string' ? search.accountUuid : undefined,
      organizationLevelUuid:
        typeof search.organizationLevelUuid === 'string'
          ? search.organizationLevelUuid
          : undefined,
      dateRange:
        typeof search.dateRange === 'string' ? search.dateRange : undefined,
      surveyType:
        typeof search.surveyType === 'string' ? search.surveyType : undefined,
      scoreCategory:
        typeof search.scoreCategory === 'string'
          ? search.scoreCategory
          : undefined,
      showAlertsOnly:
        search.showAlertsOnly === 'true' || search.showAlertsOnly === true
          ? true
          : undefined,
      showWithCommentsOnly:
        search.showWithCommentsOnly === 'true' ||
        search.showWithCommentsOnly === true
          ? true
          : undefined,
      showNotViewedOnly:
        search.showNotViewedOnly === 'true' || search.showNotViewedOnly === true
          ? true
          : undefined,
    };
  },
});

interface SurveyFilters {
  accountUuid: string;
  dateRange: string;
  surveyType: string;
  scoreCategory: string;
  showAlertsOnly: boolean;
  showWithCommentsOnly: boolean;
  showNotViewedOnly: boolean;
  organizationLevelUuid: string;
}

// Bulk actions available on the surveys list. Only "mark as reviewed" exists
// today, but the action bar is driven off this list so future actions plug in
// here (add an entry) plus a case in handleApplyBulkAction — no component edit.
const BULK_ACTION_IDS = {
  MARK_REVIEWED: 'mark-reviewed',
} as const;

const BULK_ACTIONS: BulkAction[] = [
  {
    id: BULK_ACTION_IDS.MARK_REVIEWED,
    label: 'Mark selected surveys as reviewed',
  },
];

// What the result banner needs to render after a bulk action finishes. The
// message wording and undo behaviour are the concrete action's concern, so
// they're captured here rather than baked into the (generic) banner.
interface BulkActionResult {
  message: string;
  undo?: () => Promise<void>;
}

// Helper function to flatten hierarchy tree into options array with indentation
const flattenHierarchyToOptions = (
  levels: HierarchyLevelResponse[],
  depth = 0,
): Array<{ value: string; label: string }> => {
  const options: Array<{ value: string; label: string }> = [];

  levels.forEach((level) => {
    const indent = '\u00A0\u00A0'.repeat(depth); // Non-breaking spaces for indentation
    options.push({
      value: level.uuid,
      label: `${indent}${level.name}`,
    });

    if (level.children && level.children.length > 0) {
      options.push(...flattenHierarchyToOptions(level.children, depth + 1));
    }
  });

  return options;
};

export function Surveys() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/surveys/' });
  const { addToast } = useToast();
  const { user } = useAuth();

  // Determine default organization level based on user role
  const getDefaultOrgLevel = () => {
    // For exec/managers/staff, default to their hierarchy level
    if (
      userIsAuthorized(user?.role, [
        ROLES.EXECUTIVE,
        ROLES.MANAGER,
        ROLES.STAFF,
      ]) &&
      user?.hierarchyLevel
    ) {
      return user.hierarchyLevel.uuid;
    }
    // Otherwise default to ALL
    return SURVEY_FILTER_VALUES.ORGANIZATION_LEVEL.ALL;
  };

  // Initialize filter state from URL search params
  const [filters, setFilters] = useState<SurveyFilters>({
    accountUuid: searchParams.accountUuid ?? SURVEY_FILTER_VALUES.ACCOUNT.ALL,
    dateRange: searchParams.dateRange ?? SURVEY_FILTER_VALUES.DATE_RANGE.ALL,
    surveyType: searchParams.surveyType ?? SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL,
    scoreCategory:
      searchParams.scoreCategory ?? SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL,
    showAlertsOnly: searchParams.showAlertsOnly ?? false,
    showWithCommentsOnly: searchParams.showWithCommentsOnly ?? false,
    showNotViewedOnly: searchParams.showNotViewedOnly ?? false,
    organizationLevelUuid:
      searchParams.organizationLevelUuid ?? getDefaultOrgLevel(),
  });

  // Row selection is owned by the shared Table (keyed by `${type}:${uuid}`
  // since uuid alone isn't unique across the three survey tables). We hold the
  // controlled selection map here and derive the selected survey objects.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const getSurveyRowId = (survey: SurveyTableItem) =>
    `${survey.type}:${survey.uuid}`;

  const [isApplyingBulkAction, setIsApplyingBulkAction] = useState(false);
  const [isUndoingBulkAction, setIsUndoingBulkAction] = useState(false);
  const [lastBulkActionResult, setLastBulkActionResult] =
    useState<BulkActionResult | null>(null);

  // Generic entry point wired to the action bar. Sets the shared "applying"
  // flag synchronously (this is what disables Apply and guards double-clicks)
  // and dispatches to the concrete action's handler.
  const handleApplyBulkAction = async (actionId: string) => {
    setIsApplyingBulkAction(true);
    try {
      if (actionId === BULK_ACTION_IDS.MARK_REVIEWED) {
        await applyMarkSurveysAsReviewed();
      }
    } finally {
      setIsApplyingBulkAction(false);
    }
  };

  // Concrete action: mark the selected surveys as reviewed.
  const applyMarkSurveysAsReviewed = async () => {
    try {
      const surveys: SurveyRef[] = selectedRows.map((survey) => ({
        uuid: survey.uuid,
        type: survey.type,
      }));
      const result = await bulkMarkSurveysAsReviewed(surveys);
      setRowSelection({});
      if (result.updated.length > 0) {
        // Only show the confirmation/Undo banner when something actually
        // succeeded — Undo has nothing to revert (and the server rejects an
        // empty batch) if every selected survey was skipped by a race.
        const total = result.updated.length + result.skipped.length;
        const message =
          result.skipped.length === 0
            ? `${result.updated.length} surveys marked as reviewed.`
            : `${result.updated.length} of ${total} surveys marked as reviewed — the rest were already reviewed or are no longer available.`;
        setLastBulkActionResult({
          message,
          undo: () => undoMarkSurveysAsReviewed(result.updated),
        });
      } else {
        addToast(
          'None of the selected surveys could be marked as reviewed — they may already be reviewed or no longer available.',
          { type: TOAST_TYPES.WARNING, title: 'Nothing updated' },
        );
      }
      setRefetchData(true);
    } catch (error) {
      addToast('Couldn’t mark surveys as reviewed — try again', {
        type: TOAST_TYPES.ERROR,
        title: 'Error',
      });
    }
  };

  const undoMarkSurveysAsReviewed = async (surveys: SurveyRef[]) => {
    await undoBulkMarkSurveysAsReviewed(surveys);
    addToast('Bulk review undone', { type: TOAST_TYPES.SUCCESS });
  };

  // Generic undo entry point — runs whatever undo the last action registered.
  const handleUndoBulkAction = async () => {
    if (!lastBulkActionResult?.undo || isUndoingBulkAction) return;
    setIsUndoingBulkAction(true);
    try {
      await lastBulkActionResult.undo();
      setLastBulkActionResult(null);
      setRefetchData(true);
    } catch (error) {
      addToast('Couldn’t undo — try again', {
        type: TOAST_TYPES.ERROR,
        title: 'Error',
      });
    } finally {
      setIsUndoingBulkAction(false);
    }
  };

  // Update URL when filters change (preserve pagination/sorting/search state)
  useEffect(() => {
    const newSearch: any = { ...searchParams };

    // Include filters based on role availability
    // Account filter - Only for ADMINISTRATOR
    if (userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR])) {
      newSearch.accountUuid = filters.accountUuid;
    }

    // Organization Level filter - For ADMINISTRATOR, EXECUTIVE, MANAGER, STAFF
    if (
      userIsAuthorized(user?.role, [
        ROLES.ADMINISTRATOR,
        ROLES.EXECUTIVE,
        ROLES.MANAGER,
        ROLES.STAFF,
      ])
    ) {
      newSearch.organizationLevelUuid = filters.organizationLevelUuid;
    }

    // Date Range filter - Available to all users
    newSearch.dateRange = filters.dateRange;

    // Survey Type filter - Available to all users
    newSearch.surveyType = filters.surveyType;

    // Score Category filter - For ADMINISTRATOR, EXECUTIVE, MANAGER
    if (
      userIsAuthorized(user?.role, [
        ROLES.ADMINISTRATOR,
        ROLES.EXECUTIVE,
        ROLES.MANAGER,
        ROLES.STAFF,
      ])
    ) {
      newSearch.scoreCategory = filters.scoreCategory;
    }

    // Boolean filters
    newSearch.showAlertsOnly = filters.showAlertsOnly;
    newSearch.showWithCommentsOnly = filters.showWithCommentsOnly;
    newSearch.showNotViewedOnly = filters.showNotViewedOnly;

    navigate({
      to: '/surveys',
      search: newSearch,
      replace: true,
    });
  }, [filters, user?.role, searchParams]);

  // Fetch accounts for dropdown
  const [accounts, setAccounts] = useState<AccountListItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  // Fetch hierarchy levels for dropdown
  const [hierarchyLevels, setHierarchyLevels] = useState<
    HierarchyLevelResponse[]
  >([]);
  const [isLoadingHierarchy, setIsLoadingHierarchy] = useState(false);

  // Only fetch accounts if user is admin
  useEffect(() => {
    const fetchAccountsData = async () => {
      try {
        const response = await getAccounts();
        setAccounts(response || []);
      } catch (error) {
        addToast('Failed to fetch accounts.', {
          type: TOAST_TYPES.ERROR,
        });
      }
    };
    if (userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR])) {
      fetchAccountsData();
    }
  }, []);

  // Fetch categories for admin/ executive/ staff and manager based on selected account
  // Admin can fetch categories for any account, executive and manager can only fetch for their account
  // Check is applied on backend
  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        const accountUuid =
          filters.accountUuid === SURVEY_FILTER_VALUES.ACCOUNT.ALL
            ? undefined
            : filters.accountUuid;
        const response = await getRouteSurveyCategories(accountUuid);
        setCategories(response || []);
      } catch (error) {
        addToast('Failed to fetch categories', {
          type: TOAST_TYPES.ERROR,
        });
        setCategories([]);
      }
    };
    if (
      userIsAuthorized(user?.role, [
        ROLES.ADMINISTRATOR,
        ROLES.EXECUTIVE,
        ROLES.MANAGER,
        ROLES.STAFF,
      ])
    ) {
      fetchCategoriesData();
    }
  }, [filters.accountUuid]);

  // Fetch hierarchy levels for admins (when account selected) and for exec/managers (their account)
  useEffect(() => {
    const fetchHierarchyLevels = async () => {
      // Determine which account to fetch hierarchy for
      let accountUuidToFetch: string | undefined;

      if (userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR])) {
        // Admin: only fetch if specific account is selected
        if (filters.accountUuid !== SURVEY_FILTER_VALUES.ACCOUNT.ALL) {
          accountUuidToFetch = filters.accountUuid;
        }
      } else if (
        userIsAuthorized(user?.role, [
          ROLES.EXECUTIVE,
          ROLES.MANAGER,
          ROLES.STAFF,
        ])
      ) {
        // Exec/Manager/Staff: backend will automatically use their account, pass any non-empty value
        accountUuidToFetch = 'placeholder';
      }

      if (!accountUuidToFetch) {
        setHierarchyLevels([]);
        return;
      }

      try {
        setIsLoadingHierarchy(true);
        const response = await getHierarchyLevels(accountUuidToFetch);
        setHierarchyLevels(response.levels || []);
      } catch (error) {
        addToast('Failed to fetch organization levels', {
          type: TOAST_TYPES.ERROR,
        });
        setHierarchyLevels([]);
      } finally {
        setIsLoadingHierarchy(false);
      }
    };

    if (
      userIsAuthorized(user?.role, [
        ROLES.ADMINISTRATOR,
        ROLES.EXECUTIVE,
        ROLES.MANAGER,
        ROLES.STAFF,
      ])
    ) {
      fetchHierarchyLevels();
    }
  }, [filters.accountUuid]);

  // State for triggering data refetch
  const [refetchData, setRefetchData] = useState(false);

  // State for offline mode
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isLoadingCached, setIsLoadingCached] = useState(false);
  const [cachedData, setCachedData] = useState<{
    surveys: SurveyTableItem[];
    pageCount: number;
    totalRowsCount: number;
  } | null>(null);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setRefetchData(true); // Refetch when coming back online
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load cached data when offline
  useEffect(() => {
    const loadCachedData = async () => {
      if (isOffline) {
        setIsLoadingCached(true);
        try {
          const cached = await getCachedSurveys();
          if (cached) {
            setCachedData({
              surveys: cached.surveys,
              pageCount: cached.pagination.pageCount,
              totalRowsCount: cached.pagination.total,
            });
          } else {
            setCachedData(null);
          }
        } catch (error) {
          setCachedData(null);
        } finally {
          setIsLoadingCached(false);
        }
      } else {
        setCachedData(null);
      }
    };

    loadCachedData();
  }, [isOffline]);

  // Use the useTable hook for server-side pagination, sorting, and filtering
  const {
    data,
    pageCount,
    totalRowsCount,
    isLoading,
    paginationState,
    setPaginationState,
  } = useTable<SurveyTableItem>({
    apiUrl: '/surveys',
    initialPaginationState: {
      page: 1,
      pageSize: 50,
      sortBy: 'date',
      direction: 'desc',
      searchText: '',
    },
    additionalParams: {
      account_uuid:
        filters.accountUuid !== SURVEY_FILTER_VALUES.ACCOUNT.ALL
          ? filters.accountUuid
          : undefined,
      date_range:
        filters.dateRange !== SURVEY_FILTER_VALUES.DATE_RANGE.ALL
          ? filters.dateRange
          : undefined,
      survey_type:
        filters.surveyType !== SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL
          ? filters.surveyType
          : undefined,
      score_category:
        filters.scoreCategory !== SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL
          ? filters.scoreCategory
          : undefined,
      show_alerts_only: filters.showAlertsOnly || undefined,
      show_with_comments_only: filters.showWithCommentsOnly || undefined,
      show_not_viewed_only: filters.showNotViewedOnly || undefined,
      organization_level_uuid:
        filters.organizationLevelUuid &&
        filters.organizationLevelUuid !==
          SURVEY_FILTER_VALUES.ORGANIZATION_LEVEL.ALL
          ? filters.organizationLevelUuid
          : undefined,
    },
    transformResponse: (res) => res.data,
    onSuccess: async (responseData) => {
      // Cache surveys for offline viewing (only if driver and on first page with no filters)
      if (
        userIsAuthorized(user?.role, [ROLES.DRIVER]) &&
        paginationState.page === 1 &&
        !paginationState.searchText &&
        filters.accountUuid === SURVEY_FILTER_VALUES.ACCOUNT.ALL &&
        filters.dateRange === SURVEY_FILTER_VALUES.DATE_RANGE.ALL &&
        filters.surveyType === SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL &&
        filters.scoreCategory === SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL &&
        !filters.showAlertsOnly &&
        !filters.showWithCommentsOnly &&
        !filters.showNotViewedOnly
      ) {
        try {
          await cacheSurveys(responseData.data || [], {
            page: responseData.current_page || 1,
            pageSize: responseData.page_size || 10,
            total: responseData.total_count || 0,
            pageCount: responseData.page_count || 0,
          });
        } catch (error) {
          console.error('Error caching surveys:', error);
        }
      }
    },
    onError: () => {
      addToast('Failed to fetch surveys', {
        type: TOAST_TYPES.ERROR,
      });
    },
    dataFetchControl: {
      reFetchData: refetchData,
      setRefetchData: setRefetchData,
    },
  });

  // Bulk selection (and any pending confirmation) doesn't survive a
  // page/sort/filter change, or the Comments toggle switching off — the
  // selected rows may no longer even be on screen.
  useEffect(() => {
    setRowSelection({});
    setLastBulkActionResult(null);
  }, [
    filters,
    paginationState.page,
    paginationState.sortBy,
    paginationState.direction,
  ]);

  // Callback to trigger refetch when offline surveys are synced
  const handleOfflineSyncComplete = () => {
    setRefetchData(true);
  };

  // Use cached data when offline, otherwise use fetched data
  const displayData = isOffline && cachedData ? cachedData.surveys : data;
  const selectedRows = displayData.filter(
    (survey) => rowSelection[getSurveyRowId(survey)],
  );
  const displayPageCount =
    isOffline && cachedData ? cachedData.pageCount : pageCount;
  const displayTotalRowsCount =
    isOffline && cachedData ? cachedData.totalRowsCount : totalRowsCount;
  const displayIsLoading = isOffline ? isLoadingCached : isLoading;

  // Define table columns - dynamic based on selected score category and filters
  const columns = useMemo<ColumnDef<SurveyTableItem>[]>(() => {
    const isDriverView = userIsAuthorized(user?.role, [ROLES.DRIVER]);

    const baseColumns: ColumnDef<SurveyTableItem>[] = [
      {
        header: 'Account',
        accessorKey: 'accountName',
        meta: {
          allowedRoles: [ROLES.ADMINISTRATOR],
          headerClassName: 'col-account',
          cellClassName: 'col-account clamp-cell',
        },
        cell: ({ row }) => {
          return (
            <a
              href={`/accounts/${row.original.accountUuid}`}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.stopPropagation();
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate({ to: `/accounts/${row.original.accountUuid}` });
                }
              }}
              className="link"
            >
              {row.original.accountName}
            </a>
          );
        },
      },
      {
        header: 'Type',
        accessorKey: 'type',
        meta: {
          headerClassName: 'col-type',
          // "Customer Feedback" needs ~143px unwrapped but this column sits
          // near 106px, so without the clamp its td keeps the shared
          // `white-space: nowrap` and blows the width budget on its own.
          cellClassName: 'col-type clamp-cell',
        },
        cell: ({ row }) => {
          const type = row.original.type;
          const label =
            type === SurveyType.ROUTE_SURVEY
              ? 'Route Survey'
              : type === SurveyType.INCIDENT_REPORT
              ? 'Incident Report'
              : 'Customer Feedback';
          const href =
            type === SurveyType.ROUTE_SURVEY
              ? `/route-surveys/${row.original.uuid}`
              : type === SurveyType.INCIDENT_REPORT
              ? `/incident-reports/${row.original.uuid}`
              : `/customer-feedbacks/${row.original.uuid}`;

          return (
            <a
              href={href}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.stopPropagation();
                } else {
                  e.preventDefault();
                }
              }}
              className="link"
            >
              {label}
            </a>
          );
        },
      },
      {
        header: 'Date',
        accessorKey: 'date',
        meta: {
          headerClassName: 'date-column',
          cellClassName: 'date-column',
        },
        cell: ({ getValue }) => {
          return <DateCell value={getValue() as string} format="date" />;
        },
      },
    ];

    // For drivers, add Reviewed column right after Date
    if (isDriverView) {
      baseColumns.push({
        header: 'Reviewed',
        accessorKey: 'viewed',
        meta: {
          headerClassName: 'col-reviewed',
          cellClassName: 'col-reviewed',
        },
        cell: ({ getValue }) => {
          const viewed = getValue() as boolean;
          return (
            <span className={getReviewedBadgeClasses(viewed)}>
              {viewed ? '✓' : '✗'}
            </span>
          );
        },
      });
    }

    // Add remaining base columns for non-driver roles
    baseColumns.push(
      {
        header: 'Driver',
        accessorKey: 'driverName',
        meta: {
          allowedRoles: [
            ROLES.ADMINISTRATOR,
            ROLES.EXECUTIVE,
            ROLES.MANAGER,
            ROLES.STAFF,
          ],
          headerClassName: 'col-driver',
          cellClassName: 'col-driver clamp-cell',
        },
        cell: ({ row }) => {
          return (
            <a
              href={`/organizations/${row.original.driverUuid}`}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.stopPropagation();
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate({ to: `/organizations/${row.original.driverUuid}` });
                }
              }}
              className="link"
            >
              {row.original.driverName}
            </a>
          );
        },
      },
      {
        header: 'Organization Level',
        accessorKey: 'managerOrganizationLevel',
        meta: {
          allowedRoles: [
            ROLES.ADMINISTRATOR,
            ROLES.EXECUTIVE,
            ROLES.MANAGER,
            ROLES.STAFF,
          ],
          headerClassName: 'col-org-level',
          cellClassName: 'col-org-level clamp-cell',
        },
        cell: ({ row }) => {
          return <span>{row.original.managerOrganizationLevel || 'N/A'}</span>;
        },
      },
      {
        header: 'Manager',
        accessorKey: 'managerName',
        meta: {
          allowedRoles: [
            ROLES.ADMINISTRATOR,
            ROLES.EXECUTIVE,
            ROLES.MANAGER,
            ROLES.STAFF,
          ],
          headerClassName: 'col-manager',
          cellClassName: 'col-manager clamp-cell',
        },
        cell: ({ row }) => {
          return <span>{row.original.managerName || 'N/A'}</span>;
        },
      },
    );

    // Score column is overall score if overall is selected, else category scorewith that category label
    const scoreColumnHeader =
      filters.scoreCategory === SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL
        ? 'Overall Score'
        : filters.scoreCategory;

    // Conditionally add columns based on showWithCommentsOnly toggle
    // If true, show only comment text column, else show regular columns
    if (filters.showWithCommentsOnly) {
      // Score column mirrors the Comments-off behavior: it tracks the Score
      // Category filter (Overall → overallScore, otherwise the category's
      // score) so the two stay in sync. Informational, so visible to Staff too
      // (unlike the select column, which Table restricts via
      // selectionConfig.allowedRoles).
      baseColumns.push({
        header: scoreColumnHeader,
        accessorKey:
          filters.scoreCategory === SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL
            ? 'overallScore'
            : 'categoryScore',
        meta: {
          allowedRoles: [
            ROLES.ADMINISTRATOR,
            ROLES.EXECUTIVE,
            ROLES.MANAGER,
            ROLES.STAFF,
          ],
          headerClassName: 'col-score',
          cellClassName: 'col-score',
        },
        cell: ({ getValue }) => {
          const score = getValue() as number | null;
          if (score === null) return <span>—</span>;
          return (
            <span className={getSurveyListScoreClasses(score)}>{score}</span>
          );
        },
      });

      // Show comment text column when comment toggle is on
      baseColumns.push({
        header: 'Comment Text',
        meta: {
          allowedRoles: [
            ROLES.ADMINISTRATOR,
            ROLES.EXECUTIVE,
            ROLES.MANAGER,
            ROLES.STAFF,
          ],
          headerClassName: 'comment-text-column',
          cellClassName: 'comment-text-column',
        },
        accessorKey: 'comments',
        enableSorting: false,
        cell: ({ getValue }) => {
          const comments = getValue() as
            | { label: string; text: string }[]
            | undefined;
          if (!comments || comments.length === 0) {
            return <span>-</span>;
          }

          return (
            <div className="whitespace-normal space-y-1">
              {comments.map((comment, index) => (
                <div key={index} className="break-words">
                  <span className="font-semibold">{comment.label}:</span>{' '}
                  <span className="italic">&ldquo;{comment.text}&rdquo;</span>
                </div>
              ))}
            </div>
          );
        },
      });
    } else {
      // Show regular columns when toggle is off
      baseColumns.push(
        {
          header: 'Alert Type',
          accessorKey: 'alertType',
          meta: {
            allowedRoles: [
              ROLES.ADMINISTRATOR,
              ROLES.EXECUTIVE,
              ROLES.MANAGER,
              ROLES.STAFF,
            ],
            headerClassName: 'col-alert-type',
            cellClassName: 'col-alert-type',
          },
          cell: ({ getValue }) => {
            const alertTypeLabel = getValue() as string;
            return (
              <Badge
                text={alertTypeLabel}
                type={getAlertBadgeType(alertTypeLabel)}
              />
            );
          },
        },
        {
          // Shows overall score or specific category score depending on filter
          header: scoreColumnHeader,
          accessorKey:
            filters.scoreCategory ===
            SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL
              ? 'overallScore'
              : 'categoryScore',
          meta: {
            headerClassName: 'col-score',
            cellClassName: 'col-score',
          },
          cell: ({ getValue }) => {
            const score = getValue() as number | null;
            if (score === null) return '-';
            return (
              <span className={getSurveyListScoreClasses(score)}>{score}</span>
            );
          },
        },
        {
          header: 'eNPS',
          accessorKey: 'enpsScore',
          meta: {
            headerClassName: 'col-enps',
            cellClassName: 'col-enps',
          },
          cell: ({ getValue }) => {
            const score = getValue() as number | null;
            if (score === null) return '-';
            return <span className={getEnpsScoreClasses(score)}>{score}</span>;
          },
        },
        {
          header: 'Comments',
          accessorKey: 'commentsCount',
          meta: {
            headerClassName: 'col-comments-count',
            cellClassName: 'col-comments-count',
          },
        },
      );
    }

    // For non-drivers, show Reviewed column at the end
    if (!isDriverView) {
      baseColumns.push({
        header: 'Reviewed',
        accessorKey: 'viewed',
        meta: {
          headerClassName: 'col-reviewed',
          cellClassName: 'col-reviewed',
        },
        cell: ({ getValue }) => {
          const viewed = getValue() as boolean;
          return (
            <span className={getReviewedBadgeClasses(viewed)}>
              {viewed ? '✓' : '✗'}
            </span>
          );
        },
      });
    }

    // Comments view only: a trailing flexible spacer column absorbs leftover
    // horizontal space on wide screens so the real columns pack to the left
    // (no gap between the checkbox and the first column). Styled via
    // `.spacer-column` scoped under `.surveys-comments-view`.
    if (filters.showWithCommentsOnly) {
      baseColumns.push({
        id: 'spacer',
        header: '',
        enableSorting: false,
        meta: {
          headerClassName: 'spacer-column',
          cellClassName: 'spacer-column',
          preventRowClick: true,
        },
        cell: () => null,
      });
    }

    return baseColumns;
  }, [
    filters.scoreCategory,
    filters.showWithCommentsOnly,
    categories,
    user?.role,
  ]);

  // Navigate to survey detail page on row click
  const handleSurveyView = (row: { original: SurveyTableItem }) => {
    // If offline, show toast and don't navigate
    if (isOffline) {
      addToast(
        'You can view survey details once you have internet connectivity',
        {
          type: TOAST_TYPES.INFO,
        },
      );
      return;
    }

    const surveyType = row.original.type;
    const uuid = row.original.uuid;

    if (surveyType === SurveyType.ROUTE_SURVEY) {
      navigate({ to: `/route-surveys/${uuid}` });
    } else if (surveyType === SurveyType.INCIDENT_REPORT) {
      navigate({ to: `/incident-reports/${uuid}` });
    } else if (surveyType === SurveyType.CUSTOMER_FEEDBACK) {
      navigate({ to: `/customer-feedbacks/${uuid}` });
    }
  };

  // Handle search input change
  const handleSearchChange = (searchText: string) => {
    setPaginationState((prev) => ({
      ...prev,
      searchText,
      page: 1,
    }));
  };

  // Track if component has mounted to avoid showing toast on initial render
  const hasMountedRef = useRef(false);
  useEffect(() => {
    hasMountedRef.current = true;
  }, []);

  // Wrap setPaginationState to show toast when offline
  const handleSetPaginationState = (
    update: React.SetStateAction<typeof paginationState>,
  ) => {
    if (isOffline && hasMountedRef.current) {
      addToast(
        'You are currently offline. Some features may be unavailable until you reconnect.',
        { type: TOAST_TYPES.INFO },
      );
      return;
    }
    setPaginationState(update);
  };

  // Check if filters have been changed from default
  const areFiltersChanged = useMemo(() => {
    // Default organization level depends on user role
    const defaultOrgLevel =
      userIsAuthorized(user?.role, [
        ROLES.EXECUTIVE,
        ROLES.MANAGER,
        ROLES.STAFF,
      ]) && user?.hierarchyLevel
        ? user.hierarchyLevel.uuid
        : SURVEY_FILTER_VALUES.ORGANIZATION_LEVEL.ALL;

    return (
      filters.accountUuid !== SURVEY_FILTER_VALUES.ACCOUNT.ALL ||
      filters.dateRange !== SURVEY_FILTER_VALUES.DATE_RANGE.ALL ||
      filters.surveyType !== SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL ||
      filters.scoreCategory !== SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL ||
      filters.showAlertsOnly !== false ||
      filters.showWithCommentsOnly !== false ||
      filters.showNotViewedOnly !== false ||
      filters.organizationLevelUuid !== defaultOrgLevel
    );
  }, [filters, user?.role, user?.hierarchyLevel]);

  // Reset filters to default values
  const handleResetFilters = () => {
    setFilters({
      accountUuid: SURVEY_FILTER_VALUES.ACCOUNT.ALL,
      dateRange: SURVEY_FILTER_VALUES.DATE_RANGE.ALL,
      surveyType: SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL,
      scoreCategory: SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL,
      showAlertsOnly: false,
      showWithCommentsOnly: false,
      showNotViewedOnly: false,
      organizationLevelUuid:
        user?.hierarchyLevel?.uuid ||
        SURVEY_FILTER_VALUES.ORGANIZATION_LEVEL.ALL,
    });
  };

  // Handle download as CSV
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadAsCsv = async () => {
    try {
      setIsDownloading(true);
      await downloadSurveysAsCsv({
        sortBy: paginationState.sortBy,
        sortDirection: paginationState.direction,
        searchText: paginationState.searchText,
        accountUuid:
          filters.accountUuid !== SURVEY_FILTER_VALUES.ACCOUNT.ALL
            ? filters.accountUuid
            : undefined,
        dateRange:
          filters.dateRange !== SURVEY_FILTER_VALUES.DATE_RANGE.ALL
            ? filters.dateRange
            : undefined,
        surveyType:
          filters.surveyType !== SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL
            ? filters.surveyType
            : undefined,
        scoreCategory:
          filters.scoreCategory !== SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL
            ? filters.scoreCategory
            : undefined,
        showAlertsOnly: filters.showAlertsOnly || undefined,
        showWithCommentsOnly: filters.showWithCommentsOnly || undefined,
        showNotViewedOnly: filters.showNotViewedOnly || undefined,
        organizationLevelUuid:
          filters.organizationLevelUuid &&
          filters.organizationLevelUuid !==
            SURVEY_FILTER_VALUES.ORGANIZATION_LEVEL.ALL
            ? filters.organizationLevelUuid
            : undefined,
      });
    } catch (error) {
      addToast('Failed to download surveys. Please try again.', {
        type: TOAST_TYPES.ERROR,
      });
      setIsDownloading(false);
    } finally {
      setIsDownloading(false);
    }
  };

  // Bulk-action select checkbox eligibility — Admin/Exec/Manager only. Row
  // eligibility is currently the mark-as-reviewed rule (unviewed +
  // canMarkAsViewed, computed server-side); when a second bulk action with a
  // different eligibility rule lands, this becomes per-action.
  const surveySelectionConfig: TableSelectionConfig<SurveyTableItem> = {
    canSelectRow: (survey) => survey.canMarkAsViewed && !survey.viewed,
    getDisabledReason: (survey) =>
      survey.viewed
        ? 'Already reviewed'
        : !survey.canMarkAsViewed
        ? 'Outside your organization'
        : undefined,
    allowedRoles: [
      ROLES.ADMINISTRATOR,
      ROLES.EXECUTIVE,
      ROLES.MANAGER,
      ROLES.STAFF,
    ],
    isApplying: isApplyingBulkAction,
  };

  return (
    <BaseLayout
      header={{
        headerText: userIsAuthorized(user?.role, [ROLES.DRIVER])
          ? 'Your Surveys'
          : 'Surveys',
      }}
      breadcrumbs={{
        showBreadcrumbs: false,
      }}
    >
      {/* Offline Surveys - Only for drivers */}
      <Authorizor allowedRoles={[ROLES.DRIVER]}>
        <OfflineSurveys onSyncComplete={handleOfflineSyncComplete} />
      </Authorizor>

      {/* Filters card - Hide when offline */}
      {!isOffline && (
        <Card className="mb-6">
          <CardContent>
            {/* Dropdowns and Toggles Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <Authorizor allowedRoles={[ROLES.ADMINISTRATOR]}>
                <SelectDropdown
                  id="account-filter"
                  placeholder="Account"
                  value={filters.accountUuid}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      accountUuid: e.target.value,
                      scoreCategory:
                        SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL,
                    }))
                  }
                  isActive={
                    filters.accountUuid !== SURVEY_FILTER_VALUES.ACCOUNT.ALL
                  }
                >
                  <option value={SURVEY_FILTER_VALUES.ACCOUNT.ALL}>
                    All Accounts
                  </option>
                  {accounts.map((account) => (
                    <option key={account.uuid} value={account.uuid}>
                      {account.name}
                    </option>
                  ))}
                </SelectDropdown>
              </Authorizor>
              <Authorizor
                allowedRoles={[
                  ROLES.ADMINISTRATOR,
                  ROLES.EXECUTIVE,
                  ROLES.MANAGER,
                  ROLES.STAFF,
                ]}
              >
                {/* Organization Level Filter */}
                <SelectDropdown
                  id="organization-level-filter"
                  placeholder="Organization Level"
                  value={filters.organizationLevelUuid}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      organizationLevelUuid: e.target.value,
                    }))
                  }
                  disabled={
                    (userIsAuthorized(user?.role, [ROLES.ADMINISTRATOR]) &&
                      filters.accountUuid ===
                        SURVEY_FILTER_VALUES.ACCOUNT.ALL) ||
                    isLoadingHierarchy
                  }
                  isActive={
                    filters.organizationLevelUuid !== getDefaultOrgLevel()
                  }
                >
                  <option value={SURVEY_FILTER_VALUES.ACCOUNT.ALL}>
                    All Organization Levels
                  </option>
                  {flattenHierarchyToOptions(hierarchyLevels).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectDropdown>
              </Authorizor>

              {/* Date Range Filter - Available to all users including drivers */}
              <SelectDropdown
                id="date-range-filter"
                placeholder="Date Range"
                value={filters.dateRange}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: e.target.value,
                  }))
                }
                isActive={
                  filters.dateRange !== SURVEY_FILTER_VALUES.DATE_RANGE.ALL
                }
              >
                <option value={SURVEY_FILTER_VALUES.DATE_RANGE.ALL}>
                  All Survey Dates
                </option>
                <option value={SURVEY_FILTER_VALUES.DATE_RANGE.LAST_7_DAYS}>
                  Last 7 Days
                </option>
                <option value={SURVEY_FILTER_VALUES.DATE_RANGE.LAST_30_DAYS}>
                  Last 30 Days
                </option>
              </SelectDropdown>

              {/* Survey Type Filter */}
              <SelectDropdown
                id="survey-type-filter"
                placeholder="Survey Type"
                value={filters.surveyType}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    surveyType: e.target.value,
                    // Reset score category to overall when incident report or customer feedback is selected
                    scoreCategory:
                      e.target.value ===
                        SURVEY_FILTER_VALUES.SURVEY_TYPE.INCIDENT_REPORT ||
                      e.target.value ===
                        SURVEY_FILTER_VALUES.SURVEY_TYPE.CUSTOMER_FEEDBACK
                        ? SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL
                        : prev.scoreCategory,
                  }))
                }
                isActive={
                  filters.surveyType !== SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL
                }
              >
                <option value={SURVEY_FILTER_VALUES.SURVEY_TYPE.ALL}>
                  All Survey Types
                </option>
                <option value={SURVEY_FILTER_VALUES.SURVEY_TYPE.ROUTE_SURVEY}>
                  Route Surveys
                </option>
                <option
                  value={SURVEY_FILTER_VALUES.SURVEY_TYPE.INCIDENT_REPORT}
                >
                  Incident Reports
                </option>
                <option
                  value={SURVEY_FILTER_VALUES.SURVEY_TYPE.CUSTOMER_FEEDBACK}
                >
                  Customer Feedback
                </option>
              </SelectDropdown>
              <Authorizor
                allowedRoles={[
                  ROLES.ADMINISTRATOR,
                  ROLES.EXECUTIVE,
                  ROLES.MANAGER,
                  ROLES.STAFF,
                ]}
              >
                {/* Score Category Filter */}
                <SelectDropdown
                  id="score-category-filter"
                  placeholder="Score Category"
                  value={filters.scoreCategory}
                  onChange={(e) => {
                    const newCategory = e.target.value;
                    const isSpecificCategory =
                      newCategory !==
                      SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL;
                    // Only change which score the column shows — do NOT re-sort.
                    // The list stays on its current sort (date by default); the
                    // user can still click the score column header to sort by it.
                    setFilters((prev) => ({
                      ...prev,
                      scoreCategory: newCategory,
                      // Category scores only exist for route surveys, so auto
                      // select Route Surveys when a specific category is picked.
                      surveyType: isSpecificCategory
                        ? SURVEY_FILTER_VALUES.SURVEY_TYPE.ROUTE_SURVEY
                        : prev.surveyType,
                    }));
                  }}
                  disabled={
                    filters.surveyType ===
                      SURVEY_FILTER_VALUES.SURVEY_TYPE.INCIDENT_REPORT ||
                    filters.surveyType ===
                      SURVEY_FILTER_VALUES.SURVEY_TYPE.CUSTOMER_FEEDBACK
                  }
                  isActive={
                    filters.scoreCategory !==
                    SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL
                  }
                >
                  <option value={SURVEY_FILTER_VALUES.SCORE_CATEGORY.OVERALL}>
                    Overall Score
                  </option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </SelectDropdown>

                {/* Search Input */}
                <SearchInput
                  id="surveys-search"
                  value={paginationState.searchText}
                  onChange={handleSearchChange}
                  placeholder="Search by driver name..."
                  isActive={paginationState.searchText.length > 0}
                />

                {/* Toggle Filters - Combined in one flex container */}
                <div className="flex flex-wrap items-center gap-6 lg:col-span-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="alerts-toggle"
                      checked={filters.showAlertsOnly}
                      onCheckedChange={(checked) =>
                        setFilters((prev) => ({
                          ...prev,
                          showAlertsOnly: checked,
                        }))
                      }
                    />
                    <span className="text-sm whitespace-nowrap">Alerts</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="comments-toggle"
                      checked={filters.showWithCommentsOnly}
                      onCheckedChange={(checked) =>
                        setFilters((prev) => ({
                          ...prev,
                          showWithCommentsOnly: checked,
                        }))
                      }
                    />
                    <span className="text-sm whitespace-nowrap">Comments</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      id="not-viewed-toggle"
                      checked={filters.showNotViewedOnly}
                      onCheckedChange={(checked) =>
                        setFilters((prev) => ({
                          ...prev,
                          showNotViewedOnly: checked,
                        }))
                      }
                    />
                    <span className="text-sm whitespace-nowrap">
                      Not Reviewed
                    </span>
                  </div>
                </div>
              </Authorizor>
            </div>

            {/* Action Buttons Row */}
            <div className="button-container-right">
              <Authorizor
                allowedRoles={[
                  ROLES.ADMINISTRATOR,
                  ROLES.EXECUTIVE,
                  ROLES.MANAGER,
                  ROLES.STAFF,
                ]}
              >
                <Button
                  variant="outline-primary"
                  size="small"
                  onClick={handleDownloadAsCsv}
                  disabled={isDownloading}
                >
                  {isDownloading && <Spinner size="small" />}
                  <Download size={16} />
                  Download as CSV
                </Button>
              </Authorizor>
              {areFiltersChanged && (
                <Button
                  variant="outline-secondary"
                  size="small"
                  onClick={handleResetFilters}
                >
                  Reset Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* The action bar is shown for the whole Comments view (not only once a
          row is checked). With nothing selected it shows a disabled state, or —
          right after an action ran — the result/Undo message inline. */}
      {filters.showWithCommentsOnly && (
        <BulkActionBar
          actions={BULK_ACTIONS}
          selectedCount={selectedRows.length}
          isApplying={isApplyingBulkAction}
          onApply={handleApplyBulkAction}
          onClear={() => setRowSelection({})}
          resultMessage={lastBulkActionResult?.message}
          onUndo={lastBulkActionResult?.undo ? handleUndoBulkAction : undefined}
          onDismissResult={() => setLastBulkActionResult(null)}
          isUndoing={isUndoingBulkAction}
        />
      )}

      {/* Table card */}
      <Card>
        <CardContent>
          {isOffline && !cachedData && (
            <div className="text-center py-8 text-neutral-dark-2">
              No cached surveys available. Surveys will be cached when you are
              online.
            </div>
          )}
          {(!isOffline || cachedData) && (
            <Table
              id="surveys-table"
              className={`surveys-table${
                filters.showWithCommentsOnly ? ' surveys-comments-view' : ''
              }`}
              columns={columns}
              tableData={{
                data: displayData,
                totalCount: displayTotalRowsCount,
                pageCount: displayPageCount,
                currentPageIndex: paginationState.page,
                currentPageSize: paginationState.pageSize,
              }}
              isDataLoading={displayIsLoading}
              sortConfig={{
                field: paginationState.sortBy,
                order: paginationState.direction,
              }}
              searchText={paginationState.searchText}
              onRowClick={handleSurveyView}
              setPaginationState={handleSetPaginationState}
              rowSelection={
                filters.showWithCommentsOnly ? rowSelection : undefined
              }
              onRowSelectionChange={
                filters.showWithCommentsOnly ? setRowSelection : undefined
              }
              getRowId={getSurveyRowId}
              selectionConfig={surveySelectionConfig}
            />
          )}
        </CardContent>
      </Card>
    </BaseLayout>
  );
}
